'use client';

import {
  BottomSheetTitle,
  BottomSheetTitleBox,
  BottomSheetTitleContainer,
  IcDelete,
  IconButton,
  Sheet,
  SheetContent,
  SheetPortal,
  SheetTrigger,
  Typography,
} from '@musinsa/mds';
import { SERVICE, useDeviceInfo } from '@musinsa/packages-device-detection';

import { useRouter, useSearchParams } from 'next/navigation';
import { type ReactNode, Suspense, useState } from 'react';
import { usePreventBodyScroll } from '../../common/hooks/usePreventBodyScroll';
import {
  AGREEMENT_UI_TEXT,
  AGREEMENT_VERSION_QUERY_KEY,
  type AgreementBrand,
  type AgreementType,
  getPreviousVersion,
} from '../utils/agreementVersions';
import { AgreementContent } from './AgreementContent';

interface AgreementModalProps {
  type: AgreementType;
  children: ReactNode;
}

const AgreementModalContent = ({ type, children }: AgreementModalProps) => {
  const searchParams = useSearchParams();
  const router = useRouter();
  const queryKey = AGREEMENT_VERSION_QUERY_KEY[type];
  const version = searchParams.get(queryKey) ?? undefined;

  const { service } = useDeviceInfo();
  const isBrandKnown = service !== SERVICE.UNKNOWN;
  const brand = service as AgreementBrand;

  const [isOpen, setIsOpen] = useState(false);

  usePreventBodyScroll({ open: isOpen });

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      router.replace(`${window.location.pathname}`);
    }
  };

  const handlePreviousClick = () => {
    const prev = getPreviousVersion(type, brand, version);
    if (prev) {
      router.replace(`?${queryKey}=${prev}`);
    }
  };

  return (
    <Sheet direction="bottom" defaultOpen={false} open={isOpen} onOpenChange={handleOpenChange}>
      <SheetTrigger asChild>{children}</SheetTrigger>
      <SheetPortal>
        <SheetContent className="w-screen h-full bg-white flex flex-col">
          <BottomSheetTitle>
            <BottomSheetTitleContainer className="px-4 py-3">
              <BottomSheetTitleBox>
                <Typography size="18" weight="medium">
                  {AGREEMENT_UI_TEXT[type].title}
                </Typography>
              </BottomSheetTitleBox>
              <IconButton size="size28x28" onClick={() => setIsOpen(false)}>
                <IcDelete />
              </IconButton>
            </BottomSheetTitleContainer>
          </BottomSheetTitle>

          <div className="bg-white h-full overflow-y-auto">
            <div className="px-4 py-3">
              {isOpen && isBrandKnown && (
                <AgreementContent
                  type={type}
                  brand={brand}
                  version={version}
                  onPreviousClick={handlePreviousClick}
                />
              )}
            </div>
          </div>
        </SheetContent>
      </SheetPortal>
    </Sheet>
  );
};

export const AgreementModal = ({ type, children }: AgreementModalProps) => (
  <Suspense
    fallback={
      <Typography variant="body13pxReg" colorScheme="gray-600">
        약관을 불러오는 중입니다.
      </Typography>
    }
  >
    <AgreementModalContent type={type}>{children}</AgreementModalContent>
  </Suspense>
);
